import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from './activity.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ValidationActivity } from 'app/model/pipeline/validation.activity';
import { Dataset } from 'app/model/dataset/dataset';
import { fillParameterValuesForResource, convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import { FileTypeDataset } from '../../dataset/file-type.dataset';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ValidationActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            timeout: '*',
            sleep: '*',
            dataset: '*',
            datasetSettings: '*',
            minimumSize: '*',
            childItems: '*'
        }
    }
})
export class ValidationActivityAdapter extends ActivityAsyncAdapter<ValidationActivity, Model.ValidationActivity> {

    public model(): IModel {
        return Metamodel.modelFor(ValidationActivity);
    }

    protected async _toResource(activity: ValidationActivity): Promise<Model.ValidationActivity> {
        const modelValidationActivity: Model.ValidationActivity = super._getCommonActivityProperties(activity, Metamodel.modelFor(ValidationActivity).name) as Model.ValidationActivity;
        modelValidationActivity.typeProperties = modelValidationActivity.typeProperties || { dataset: undefined };

        if (activity.validationTimeout) {
            modelValidationActivity.typeProperties.timeout = activity.validationTimeout;
        }
        modelValidationActivity.typeProperties.sleep = ValueAdapter.toResource(activity, ValidationActivity.SLEEP);

        if (activity.dataset) {
            if (this.context.adapterConfig.inlineDataset) {
                // Trident case only
                const datasetModel = Metamodel.modelOf(activity.dataset);
                const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
                if (datasetAdapter) {
                    modelValidationActivity.typeProperties.datasetSettings = (await datasetAdapter.toResource(activity.dataset)).properties;
                } else {
                    this._logError('Cannot find dataset adapter in ValidationActivityAdapter', {
                        datasetName: activity.dataset.name,
                        datasetType: datasetModel?.name,
                    });
                }
            } else {
                const datasetReference = ReferenceAdapter.toResource(activity.dataset as Dataset, this.context) as Model.DatasetReference;
                datasetReference.parameters = fillParameterValuesForResource(activity.datasetParameters, this.context,
                    this.ensureLogger());
                modelValidationActivity.typeProperties.dataset = datasetReference;

                if (activity.dataset instanceof FileTypeDataset) {
                    const fileName = activity.dataset.fileName;
                    if ((activity.childItems !== undefined) && !fileName && !FileTypeDataset.FILE_NAME.templateValue(activity.dataset)) {
                        modelValidationActivity.typeProperties.childItems = ValueAdapter.toResource(activity, ValidationActivity.CHILD_ITEMS);
                    }
                    if (activity.minimumSize && (!!fileName || !!FileTypeDataset.FILE_NAME.templateValue(activity.dataset))) {
                        modelValidationActivity.typeProperties.minimumSize = ValueAdapter.toResource(activity, ValidationActivity.MINIMUM_SIZE);
                    }
                } else if (activity.dataset instanceof FormatDatasetV2 && activity.dataset.location) {
                    if ((activity.childItems !== undefined) && activity.dataset.location.isFolder()) {
                        modelValidationActivity.typeProperties.childItems = ValueAdapter.toResource(activity, ValidationActivity.CHILD_ITEMS);
                    }
                    if (activity.minimumSize && !activity.dataset.location.isFolder()) {
                        modelValidationActivity.typeProperties.minimumSize = ValueAdapter.toResource(activity, ValidationActivity.MINIMUM_SIZE);
                    }
                }
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelValidationActivity, parsed);
        }

        return modelValidationActivity;
    }

    protected async _toModel(modelActivity: Model.ValidationActivity): Promise<ValidationActivity> {
        const activity: ValidationActivity = ValidationActivity.MODEL.create<ValidationActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { dataset: undefined };
        activity.validationTimeout = modelActivity.typeProperties.timeout;

        ValueAdapter.toModel(activity, ValidationActivity.SLEEP, modelActivity.typeProperties.sleep);
        if (modelActivity.typeProperties.childItems !== undefined) {
            ValueAdapter.toModel(activity, ValidationActivity.CHILD_ITEMS, modelActivity.typeProperties.childItems);
        }
        if (modelActivity.typeProperties.minimumSize) {
            ValueAdapter.toModel(activity, ValidationActivity.MINIMUM_SIZE, modelActivity.typeProperties.minimumSize);
        }

        if (modelActivity.typeProperties.dataset) {
            activity.dataset = ReferenceAdapter.toProxyModel<Dataset>(Metamodel.modelFor(Dataset), modelActivity.typeProperties.dataset, this.context);
            if (modelActivity.typeProperties.dataset.parameters) {
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts, activity.dataset, activity.datasetParameters, modelActivity.typeProperties.dataset.parameters);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, ValidationActivity.MODEL));
        return activity;
    }
}
